import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useChurch, useImportCalendar } from '../hooks/useChurches'
import type { CalendarImportResult } from '../hooks/useChurches'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard } from '../components/admin/AdminAtoms'
import { ApiError } from '../lib/api'
import { Copy, Check } from 'lucide-react'

// ─── Step list ────────────────────────────────────────────────────────────────

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 items-start">
      <span className="shrink-0 w-5 h-5 rounded-full bg-s2 border border-line text-[11px] font-semibold text-ink-2 flex items-center justify-center mt-0.5">
        {n}
      </span>
      <span className="text-[13px] text-ink-2 leading-[1.5]">{children}</span>
    </div>
  )
}

// ─── Eyebrow chip ─────────────────────────────────────────────────────────────

function Chip({ label }: { label: string }) {
  return (
    <span className="inline-block text-[10px] font-semibold uppercase tracking-[0.1em] text-muted bg-s2 border border-line-soft rounded-full px-2.5 py-0.5 mb-3">
      {label}
    </span>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ChurchCalendarPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: churchData, isLoading: churchLoading } = useChurch(slug)
  const church = churchData?.data

  const feedUrl = `${window.location.origin}/api/v1/churches/${slug}/calendar.ics`
  const webcalUrl = feedUrl.replace(/^https?:/, 'webcal:')

  const [copied, setCopied] = useState(false)
  const [icalUrl, setIcalUrl] = useState('')
  const [importResult, setImportResult] = useState<CalendarImportResult | null>(null)
  const [importError, setImportError] = useState<string | null>(null)

  const importCalendar = useImportCalendar(church?.id ?? '')

  function copy() {
    void navigator.clipboard.writeText(feedUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleImport(e: React.FormEvent) {
    e.preventDefault()
    setImportResult(null)
    setImportError(null)
    importCalendar.mutate(icalUrl.trim(), {
      onSuccess: (res) => { setImportResult(res.data); setIcalUrl('') },
      onError: (err) => {
        if (err instanceof ApiError) setImportError(err.message)
        else setImportError(err instanceof Error ? err.message : 'Import failed')
      },
    })
  }

  if (churchLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-[13px] text-muted">Loading…</div>
      </div>
    )
  }

  if (!church) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-[13px] text-muted">Church not found.</div>
      </div>
    )
  }

  return (
    <AdminShell>
      <AdminPageHeader
        title="Calendar sync"
        sub="Keep Google Calendar and Fellowship in sync"
      />

      <div className="flex flex-col gap-5 max-w-[680px]">

        {/* ── Export card ─────────────────────────────────────────────────── */}
        <AdminCard>
          <Chip label="Export" />
          <h2 className="serif text-[20px] font-semibold text-ink m-0 mb-2">
            Add Fellowship events to your calendar
          </h2>
          <p className="text-[13px] text-ink-2 leading-[1.6] m-0 mb-5">
            Any event you create or import in Fellowship will automatically appear in your
            connected calendar app — no manual syncing needed.
          </p>

          {/* Step 1 — primary subscribe */}
          <div className="mb-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted mb-2">
              Step 1 — Google Calendar
            </div>
            <a
              href={webcalUrl}
              className="flex items-center justify-center h-10 rounded-lg bg-primary text-on-primary text-[13px] font-semibold no-underline mb-1.5"
            >
              Subscribe in Google Calendar
            </a>
            <p className="text-[11px] text-muted m-0 text-center">
              Opens Google Calendar. Click "Add" when prompted.
            </p>
          </div>

          {/* Step 2 — manual URL for Apple / Outlook */}
          <div className="border-t border-line-soft pt-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted mb-2">
              Step 2 — Apple Calendar, Outlook, or any other app
            </div>
            <p className="text-[13px] text-ink-2 m-0 mb-2.5">
              Copy the feed URL and paste it into your calendar app's{' '}
              <span className="font-medium text-ink">"Subscribe by URL"</span> or{' '}
              <span className="font-medium text-ink">"Add calendar from URL"</span> option.
            </p>
            <div className="flex gap-1.5 items-center">
              <code className="flex-1 text-[11px] text-ink-2 bg-s2 border border-line-soft rounded-md px-2.5 py-2 overflow-hidden text-ellipsis whitespace-nowrap font-mono">
                {feedUrl}
              </code>
              <button
                type="button"
                onClick={copy}
                className="shrink-0 flex items-center gap-1.5 h-9 px-3 rounded-md text-[12px] border border-line bg-s2 cursor-pointer font-sans font-medium"
                style={{ color: copied ? 'var(--primary)' : undefined }}
              >
                {copied
                  ? <><Check size={13} /> Copied</>
                  : <><Copy size={13} /> Copy</>
                }
              </button>
            </div>
          </div>
        </AdminCard>

        {/* ── Import card ─────────────────────────────────────────────────── */}
        <AdminCard>
          <Chip label="Import" />
          <h2 className="serif text-[20px] font-semibold text-ink m-0 mb-2">
            Pull your Google Calendar events into Fellowship
          </h2>
          <p className="text-[13px] text-ink-2 leading-[1.6] m-0 mb-5">
            Already posting events to Google Calendar? Import them here — we'll create
            Fellowship listings for each upcoming event.
          </p>

          {/* How to find the URL */}
          <div className="rounded-xl border border-line-soft bg-s2 px-4 py-4 flex flex-col gap-3 mb-5">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
              How to find your iCal URL
            </div>
            <Step n={1}>Open Google Calendar in a desktop browser</Step>
            <Step n={2}>Click the <span className="font-medium text-ink">⚙ gear icon</span> → <span className="font-medium text-ink">Settings</span></Step>
            <Step n={3}>In the left panel, click your church's calendar name under <span className="font-medium text-ink">"My calendars"</span></Step>
            <Step n={4}>Scroll down to <span className="font-medium text-ink">"Integrate calendar"</span></Step>
            <Step n={5}>Copy the <span className="font-medium text-ink">"Secret address in iCal format"</span> URL</Step>
          </div>

          {/* Import form */}
          <form onSubmit={handleImport} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-[12px] font-semibold text-ink-2">iCal URL</label>
              <input
                type="url"
                value={icalUrl}
                onChange={(e) => {
                  setIcalUrl(e.target.value)
                  setImportResult(null)
                  setImportError(null)
                }}
                placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
                className="px-3 py-2.5 rounded-lg border border-line-soft bg-bg text-[13px] font-sans w-full"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={importCalendar.isPending || !icalUrl.trim()}
                className="px-4 py-2.5 rounded-lg border border-line bg-s2 text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50"
              >
                {importCalendar.isPending ? 'Importing…' : 'Import events'}
              </button>
            </div>
          </form>

          {importError && (
            <div className="mt-3 px-3 py-2.5 rounded-lg border border-danger-line bg-danger-soft text-danger-deep text-[13px]">
              {importError}
            </div>
          )}

          {importResult && (
            <div className="mt-3 px-4 py-3 rounded-xl border border-line-soft bg-s2 text-[13px]">
              <div className="font-semibold text-good-deep mb-0.5">
                ✓ {importResult.imported} event{importResult.imported !== 1 ? 's' : ''} imported
                {importResult.skipped > 0 && (
                  <span className="font-normal text-muted ml-2">· {importResult.skipped} skipped</span>
                )}
              </div>
              {importResult.errors.length > 0 && (
                <ul className="m-0 pl-4 mt-2 text-danger-deep text-[12px] flex flex-col gap-0.5">
                  {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}
        </AdminCard>

      </div>
    </AdminShell>
  )
}
